/**
 * Landscape areas and lines of the Mecklenburg-Vorpommern corporate design
 * (Markenhandbuch 4.4): colored areas that push into the format from below
 * and lines that run out of it on both sides. Every shape derives from one
 * base curve, which may be scaled freely and flipped horizontally, and all
 * shapes used together must have different colors.
 *
 * The base curve is the meadow edge of the official Bildzeichen
 * (src/assets/mv-bildzeichen.svg), normalised to a 100-unit-wide box: it
 * starts low on the left, rises and levels out to the right. Replace it with
 * the official base line from the MV download area once it is available.
 */

// Base curve, 100 units wide and BASE_HEIGHT units tall (0 = highest point).
const BASE_HEIGHT = 11.12;
const BASE_CURVE = [
  ['M', 0, 10.30],
  ['L', 19.67, 9.45],
  ['C', 31.56, 7.48, 50.97, 1.16, 66.38, 0.3],
  ['C', 70.27, 0.09, 73.87, 0, 77.15, 0],
  ['L', 100, 1.67],
];

export const SEARCH_CURVE = [
  ['M', 0, 10.30],
  ['L', 19.67, 9.45],
  ['C', 31.56, 7.48, 50.97, 1.16, 66.38, 0.3],
  ['C', 70.27, 0.09, 73.87, 0, 77.15, 0],
  ['L', 100, 1.5],
];

// The curve placed at `top` with an amplitude of `amplitude` units, in a
// 100x100 box, optionally mirrored.
function curvePath({
  top,
  amplitude,
  flip = false,
  baseCurve = BASE_CURVE,
}) {
  const scale = amplitude / BASE_HEIGHT;
  const x = (value) => (flip ? 100 - value : value);
  const y = (value) => top + value * scale;

  return baseCurve
    .map(([command, ...values]) => {
      const points = [];

      for (let index = 0; index < values.length; index += 2) {
        points.push(
          `${x(values[index]).toFixed(2)} ${y(values[index + 1]).toFixed(2)}`
        );
      }

      return `${command}${points.join(' ')}`;
    })
    .join(' ');
}

// A landscape area: the curve, closed down to the bottom of the box. It
// starts and ends outside the box so the edges always run out of the format.
function areaPath(band) {
  return `${curvePath(band)} L100 100 L0 100 Z`;
}

/**
 * Stacked landscape areas, drawn back to front. Each band: `color` (a CSS
 * color, normally a var(--mv-*)), `top` (0-100, where the curve's highest
 * point sits), `amplitude` (curve height in the same units) and `flip`.
 * Pass `lines` for landscape lines: the same shape as a stroke.
 */
export function MvLandscape({ bands = [], lines = [], className = '' }) {
  return (
    <svg
      className={['mv-landscape', className].filter(Boolean).join(' ')}
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      aria-hidden="true"
      focusable="false"
    >
      {bands.map((band, index) => (
        <path key={`area-${index}`} d={areaPath(band)} fill={band.color} />
      ))}

      {lines.map((line, index) => (
        <path
          key={`line-${index}`}
          d={curvePath(line)}
          fill="none"
          stroke={line.color}
          strokeWidth={line.width ?? 2}
          vectorEffect="non-scaling-stroke"
        />
      ))}
    </svg>
  );
}

// MV colors that may carry a generated cover; each cover takes three
// different ones, so neighbouring areas never share a color.
const COVER_PALETTE = [
  'var(--mv-wasserblau)',
  'var(--mv-himmelblau)',
  'var(--mv-sonnengelb)',
  'var(--mv-wiesengruen)',
  'var(--mv-ziegelrot)',
  'var(--mv-eisblau)',
  'var(--mv-lichtgruen)',
  'var(--mv-waldgruen)',
];

function hashString(value) {
  let hash = 0;

  for (const character of String(value ?? '')) {
    hash = (hash * 31 + character.codePointAt(0)) >>> 0;
  }

  return hash;
}

/**
 * A cover for a publication that has no image of its own: white stage with
 * three landscape areas in MV colors. The colors and the direction follow
 * from `seed` (e.g. the title), so the same publication always gets the same
 * cover and neighbouring tiles differ.
 */
export function MvCover({ seed, className = '' }) {
  const hash = hashString(seed);
  const pick = (offset) =>
    COVER_PALETTE[(hash + offset * 3) % COVER_PALETTE.length];
  const flip = hash % 2 === 1;

  return (
    <MvLandscape
      className={['mv-cover', className].filter(Boolean).join(' ')}
      bands={[
        { color: pick(0), top: 30, amplitude: 22, flip },
        { color: pick(1), top: 52, amplitude: 18, flip: !flip },
        { color: pick(2), top: 72, amplitude: 14, flip },
      ]}
    />
  );
}

export default MvLandscape;
